import type React from "react";
import { useId, useMemo } from "react";
import AreaChartStoreContext from "./contexts/AreaChartStoreContext";
import AreaChartActionsContext from "./contexts/AreaChartActionsContext";
import { DEFAULT_MARGINS, Y_AXIS_DEFAULT } from "../constants";
import Svg from "../primitives/Svg";
import XAxis from "../primitives/XAxis";
import YAxis from "../primitives/YAxis";
import Area from "../primitives/Area";
import Cursor from "../primitives/Cursor";
import FocusablePoints from "../primitives/FocusablePoints";
import ReferenceLine from "../primitives/ReferenceLine";
import type { ReferenceLineProps } from "../primitives/ReferenceLine";
import type { Margin } from "types/charts";
import type { AreaChartActions, AreaChartStore, CursorClickEvent, Series } from "./AreaChartProvider.types";
import DrawingProvider from "../DrawingProvider";
import Tooltip from "../primitives/Tooltip";
import TooltipProvider from "../TooltipProvider";
import type { TooltipData } from "../TooltipProvider";
import type { XAxisType } from "../XAxisProvider";
import XAxisProvider from "../XAxisProvider";
import YAxisProvider from "../YAxisProvider";
import type { YAxisType } from "../YAxisProvider";

export interface AreaChartProviderProps<T> {
  children?: React.ReactNode;
  width: number;
  height: number;
  margin?: Margin;
  series: Series<T>[];
  onCursorClick?: CursorClickEvent;
  yAxis?: YAxisType[];
  xAxis?: XAxisType[];
  referenceLines?: ReferenceLineProps[];
  xAccessor: (d: T) => Date;
  yAccessor: (d: T) => number;
  renderTooltip?: (data: TooltipData<T>) => React.ReactNode;
  /** Makes each x-axis date a keyboard-focusable point, named by this label, that opens the tooltip. */
  getPointLabel?: (date: Date) => string;
}

// Module-level defaults keep the store memo stable when these props are omitted.
const EMPTY_X_AXIS: XAxisType[] = [];
const EMPTY_REFERENCE_LINES: ReferenceLineProps[] = [];
const renderNoTooltip = () => null;

export function AreaChartProvider<T extends unknown>(props: AreaChartProviderProps<T>) {
  const {
    height,
    width,
    series,
    margin = DEFAULT_MARGINS,
    xAccessor,
    yAccessor,
    onCursorClick,
    yAxis = Y_AXIS_DEFAULT,
    xAxis = EMPTY_X_AXIS,
    referenceLines = EMPTY_REFERENCE_LINES,
    renderTooltip = renderNoTooltip,
    getPointLabel,
  } = props;

  const tooltipId = useId();

  const actions = useMemo(
    () =>
      ({
        xAccessor,
        yAccessor,
        onCursorClick,
      }) as AreaChartActions<unknown>,
    [onCursorClick, xAccessor, yAccessor]
  );

  const store = useMemo<AreaChartStore<T>>(
    () => ({
      width,
      height,
      margin,
      series,
      yAxis,
      xAxis,
      referenceLines,
    }),
    [width, height, margin, series, yAxis, xAxis, referenceLines]
  );

  return (
    <AreaChartStoreContext.Provider value={store}>
      <AreaChartActionsContext.Provider value={actions}>
        <DrawingProvider>
          <XAxisProvider>
            <YAxisProvider>
              <TooltipProvider>
                <Svg>
                  <XAxis />
                  <YAxis />
                  {referenceLines.map((item, i) => (
                    <ReferenceLine key={i} {...item} />
                  ))}
                  {series.map((item, i) => (
                    <Area key={i} {...item} />
                  ))}
                  <Cursor />
                  {getPointLabel ? <FocusablePoints tooltipId={tooltipId} getPointLabel={getPointLabel} /> : null}
                  {props.children}
                </Svg>
                <Tooltip id={tooltipId} renderItem={renderTooltip} />
              </TooltipProvider>
            </YAxisProvider>
          </XAxisProvider>
        </DrawingProvider>
      </AreaChartActionsContext.Provider>
    </AreaChartStoreContext.Provider>
  );
}

export default AreaChartProvider;
